import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, BookOpen, PenTool, TrendingUp, Users, Star, ArrowRight, Sparkles, Mic, Wand2, Brain, Zap, Monitor, PenLine } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { LandingNav } from '../components/landing-nav';
import { LandingFooter } from '../components/landing-footer';
import { Ceremonies } from '../components/landing-ceremonies';
import { cn } from '../lib/utils';
import { useCurrency } from '../lib/pricing';

// ---------------------------------------------------------------------------
// MoonGlowBackground — fixed ambient layer with glowing orbs and star sparks
// ---------------------------------------------------------------------------

// Deterministic star positions (no Math.random in render).
// Each entry: [x%, y%, size-rem, opacity-max, duration-s, delay-s]
const STAR_DATA: ReadonlyArray<readonly [number, number, number, number, number, number]> = [
  [4,   8,  0.20, 0.55,  3.8, 0.0],
  [11,  22, 0.16, 0.40,  4.5, 0.7],
  [19,  5,  0.22, 0.60,  3.2, 1.4],
  [26,  38, 0.14, 0.35,  5.1, 0.3],
  [33,  14, 0.18, 0.50,  4.0, 2.0],
  [40,  60, 0.20, 0.45,  3.6, 0.9],
  [47,  3,  0.16, 0.65,  4.8, 1.7],
  [54,  28, 0.22, 0.40,  3.3, 0.5],
  [61,  72, 0.14, 0.55,  5.4, 2.3],
  [68,  18, 0.18, 0.50,  4.2, 1.1],
  [75,  44, 0.20, 0.35,  3.9, 0.2],
  [82,  9,  0.16, 0.60,  4.6, 1.8],
  [89,  55, 0.22, 0.45,  3.5, 0.6],
  [95,  31, 0.14, 0.50,  5.0, 2.1],
  [8,   79, 0.18, 0.40,  4.3, 1.3],
  [17,  63, 0.20, 0.55,  3.7, 0.4],
  [23,  88, 0.16, 0.35,  5.2, 1.9],
  [31,  50, 0.22, 0.60,  4.1, 0.8],
  [38,  82, 0.14, 0.45,  3.4, 2.5],
  [45,  42, 0.18, 0.50,  4.7, 0.1],
  [52,  91, 0.20, 0.40,  3.8, 1.6],
  [59,  57, 0.16, 0.55,  5.3, 0.7],
  [66,  35, 0.22, 0.35,  4.0, 2.2],
  [73,  80, 0.14, 0.60,  3.6, 1.0],
  [80,  67, 0.18, 0.45,  4.5, 0.3],
  [87,  20, 0.20, 0.50,  3.2, 1.5],
  [93,  75, 0.16, 0.40,  5.1, 2.4],
  [6,   46, 0.22, 0.55,  4.4, 0.9],
  [14,  33, 0.14, 0.35,  3.9, 1.2],
  [21,  70, 0.18, 0.60,  4.8, 0.0],
  [29,  16, 0.20, 0.45,  3.3, 2.0],
  [36,  95, 0.16, 0.50,  5.0, 0.6],
  [43,  25, 0.22, 0.40,  4.2, 1.7],
  [50,  76, 0.14, 0.55,  3.7, 0.4],
  [57,  48, 0.18, 0.35,  4.6, 2.3],
  [63,  12, 0.20, 0.60,  3.5, 1.1],
];

// Three large ambient orbs: [x%, y%, size-rem, primary/accent selector]
type OrbColorKey = 'primary' | 'accent';
const ORB_DATA: ReadonlyArray<readonly [number, number, number, OrbColorKey]> = [
  [15, 20, 44, 'primary'],
  [78, 12, 52, 'accent'],
  [55, 78, 40, 'primary'],
];

export function MoonGlowBackground() {
  const shouldReduceMotion = useReducedMotion();

  // Breathing animation for orbs — slow scale + opacity pulse
  const orbAnimate = (i: number) =>
    shouldReduceMotion
      ? {}
      : {
          opacity: [0.06, 0.13, 0.06],
          scale: [1, 1.06, 1],
        };

  const orbTransition = (i: number) => ({
    duration: 6 + i * 1.5,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay: i * 1.2,
  });

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 -z-10 pointer-events-none overflow-hidden"
    >
      {/* Dark-mode vignette — soft radial falloff into the deep indigo void,
          adds depth without touching any section's own background/colours. */}
      <div
        className="absolute inset-0 hidden dark:block"
        style={{
          background:
            'radial-gradient(ellipse 90% 65% at 50% 0%, transparent 0%, rgba(4,6,16,0.4) 100%)',
        }}
      />

      {/* Large ambient glow orbs */}
      {ORB_DATA.map(([x, y, size, colorKey], i) => (
        <motion.div
          key={`orb-${i}`}
          className={
            colorKey === 'primary'
              ? 'absolute rounded-full bg-primary blur-[72px] will-change-transform'
              : 'absolute rounded-full bg-accent blur-[72px] will-change-transform'
          }
          style={{
            left: `${x}%`,
            top: `${y}%`,
            width: `${size}rem`,
            height: `${size}rem`,
            transform: 'translate(-50%, -50%)',
            // Light mode: very subtle (orbs are pale blue on pale bg)
            // Dark mode handled via opacity alone — dark theme bg is #0C1020 so
            // the primary (#dbe5ff) glows beautifully at low opacity
            opacity: 0.08,
          }}
          animate={orbAnimate(i)}
          transition={orbTransition(i)}
        />
      ))}

      {/* Star sparks */}
      {STAR_DATA.map(([x, y, size, opacityMax, duration, delay], i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute rounded-full bg-primary will-change-transform"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            width: `${size}rem`,
            height: `${size}rem`,
            // Even-indexed stars use accent tint via filter; odd use primary
            filter: i % 3 === 0 ? 'hue-rotate(30deg)' : undefined,
          }}
          animate={
            shouldReduceMotion
              ? { opacity: opacityMax * 0.4 }
              : {
                  opacity: [0.07, opacityMax, 0.07],
                  scale: [0.8, 1.2, 0.8],
                }
          }
          transition={
            shouldReduceMotion
              ? {}
              : {
                  duration,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay,
                }
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ChapterCardStack — animated stacked manuscript cards
// ---------------------------------------------------------------------------
export function ChapterCardStack() {
  const { t } = useTranslation();
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  const manuscriptSheets = [
    {
      chapter: t('landing.heroCards.chapter1Title', 'Capítulo I: O Sussurro das Sombras'),
      genre: t('landing.heroCards.chapter1Genre', 'Mistério & Noir'),
      text: t(
        'landing.heroCards.chapter1Text',
        'A bruma fria subia densa do cais da Baía dos Silêncios, engolindo os paralelepípedos úmidos e abafando o som das poucas carruagens que ainda ousavam circular àquela hora tardia. Helena mantinha as mãos firmes no casaco de lã, onde o metal frio de uma chave esquecida parecia pulsar mil segredos sob a ponta de seus dedos trêmulos.',
      ),
      meta: t('landing.heroCards.chapter1Meta', 'Diego Sierra • 24.500 palavras'),
    },
    {
      chapter: t('landing.heroCards.chapter2Title', 'Capítulo II: O Guardião do Farol'),
      genre: t('landing.heroCards.chapter2Genre', 'Ficção Especulativa'),
      text: t(
        'landing.heroCards.chapter2Text',
        'Do alto da torre circular de ferro forjado, o feixe prateado cortava o oceano como um gládio de luz pura. Jonas conhecia cada rangido da escada helicoidal, cada sussurro do vento norte que uivava contra os painéis reforçados de vidro. Ele sabia que, antes do amanhecer, a maré traria algo que mudaria a baía para sempre.',
      ),
      meta: t('landing.heroCards.chapter2Meta', 'M. Silveira • 18.200 palavras'),
    },
    {
      chapter: t('landing.heroCards.chapter3Title', 'Capítulo III: A Linha do Horizonte'),
      genre: t('landing.heroCards.chapter3Genre', 'Romance & Drama'),
      text: t(
        'landing.heroCards.chapter3Text',
        'Nunca fomos bons em dizer adeus. O trem aguardava na plataforma úmida, a fumaça subindo preguiçosa sob as claraboias de ferro da velha estação central. Sofia segurava o manuscrito inacabado contra o peito, sabendo que as palavras ali escritas eram a única ponte viva que restaria entre nós quando a locomotiva partisse.',
      ),
      meta: t('landing.heroCards.chapter3Meta', 'Aline Schmidt • 32.100 palavras'),
    },
  ];
  const len = manuscriptSheets.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSheetIndex((prev) => (prev + 1) % len);
    }, 3500);
    return () => clearInterval(timer);
  }, [len]);

  return (
    <div className="relative w-full max-w-[460px] h-[440px] md:h-[520px] mx-auto">
      {manuscriptSheets.map((sheet, index) => {
        const difference = (index - activeSheetIndex + len) % len;

        const zIndex = 30 - difference;
        const rotateZ = difference === 0 ? '-1deg' : difference === 1 ? '1deg' : '3deg';
        const scale = difference === 0 ? 1 : difference === 1 ? 0.96 : 0.92;
        const translateY = difference === 0 ? 0 : difference === 1 ? 16 : 32;
        const translateX = difference === 0 ? 0 : difference === 1 ? 12 : 24;
        const opacity = difference === 0 ? 1 : difference === 1 ? 0.75 : 0.45;

        return (
          <div
            key={index}
            style={{
              zIndex,
              transform: `translate(${translateX}px, ${translateY}px) scale(${scale}) rotate(${rotateZ})`,
              opacity,
              transition: 'all 0.8s cubic-bezier(0.25, 1, 0.5, 1)',
            }}
            className="absolute inset-0 rounded-2xl p-6 sm:p-8 flex flex-col justify-between border select-none bg-card border-border shadow-2xl"
          >
            {/* Card header */}
            <div>
              <div className="flex justify-between items-center pb-4 border-b border-border mb-6 font-mono text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1.5 uppercase font-bold text-primary">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                  </span>
                  {sheet.genre}
                </span>
                <span>{sheet.meta}</span>
              </div>

              <h3 className="text-lg sm:text-xl font-serif font-bold text-primary mb-4">
                {sheet.chapter}
                {difference === 0 && <span className="hero-caret" aria-hidden />}
              </h3>

              <p className="font-serif text-xs leading-relaxed line-clamp-6 text-muted-foreground">
                <span className="text-3xl float-left font-bold mr-2 mt-0.5 leading-none font-serif text-foreground">
                  {sheet.text.charAt(0)}
                </span>
                {sheet.text.slice(1)}
              </p>
            </div>

            {/* Card footer */}
            <div className="pt-4 border-t border-border flex justify-between items-center text-[10px] font-mono text-muted-foreground uppercase">
              <span>{t('landing.heroCards.moduledByMoonlight', 'Modulado ao luar')}</span>
              <span className="px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">
                {t('landing.heroCards.activeDraft', 'Rascunho Ativo')}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroSky — the hero's own sky "breathes": stars ignite in a deterministic
// cascade on mount, then twinkle gently forever. Sits between the background
// image overlay and the text content, respects prefers-reduced-motion.
// ---------------------------------------------------------------------------
const HERO_STAR_DATA: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [6, 14, 0.16, 4.2, 0.0], [14, 32, 0.12, 5.0, 0.35], [23, 8, 0.18, 3.6, 0.7],
  [31, 46, 0.10, 4.6, 0.15], [40, 20, 0.14, 5.4, 1.05], [49, 58, 0.16, 3.9, 0.5],
  [58, 12, 0.12, 4.8, 1.4], [67, 36, 0.18, 4.1, 0.9], [76, 24, 0.10, 5.2, 1.75],
  [85, 50, 0.14, 3.8, 1.2], [92, 16, 0.16, 4.5, 2.1], [11, 62, 0.10, 5.0, 1.6],
];

function HeroSky() {
  const shouldReduceMotion = useReducedMotion();
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[3] overflow-hidden">
      {HERO_STAR_DATA.map(([x, y, size, duration, delay], i) => (
        <motion.div
          key={i}
          className={i % 2 === 0 ? 'absolute rounded-full bg-accent' : 'absolute rounded-full bg-primary'}
          style={{ left: `${x}%`, top: `${y}%`, width: `${size}rem`, height: `${size}rem` }}
          animate={
            shouldReduceMotion
              ? { opacity: 0.35 }
              : { opacity: [0, 0.85, 0.3, 0.7, 0.3], scale: [0.6, 1.2, 0.9, 1.1, 0.9] }
          }
          transition={
            shouldReduceMotion
              ? {}
              : { duration, repeat: Infinity, ease: 'easeInOut', delay }
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
export const Hero = ({ onSignup }: { onSignup: () => void }) => {
  const { t } = useTranslation();
  return (
    <section className="relative pt-28 pb-16 lg:pt-28 lg:pb-24 overflow-hidden bg-background">
      {/* Moonlit hero background image — subtle in light, glowing in dark */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <img
          src="/hero-bg.webp"
          alt=""
          className="h-full w-full object-cover object-center opacity-55 dark:opacity-80"
        />
        {/* Theme-aware overlay for legibility + fade into the page background */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/35 to-background dark:from-background/45 dark:via-background/35 dark:to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-transparent to-transparent dark:from-background/40" />
      </div>
      {/* The sky breathes: cascading stars, subtle in light mode, alive in dark */}
      <HeroSky />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className="
            flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-3 items-center
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            lg:grid lg:grid-cols-2 lg:gap-8 lg:overflow-visible lg:mx-0 lg:px-0 lg:pb-0
          "
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="snap-center shrink-0 w-[88%] lg:w-auto lg:max-w-2xl lg:shrink"
          >
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-6 bg-accent/10 text-accent">
              <Star size={12} className="fill-current" />
              <span>{t('landing.hero.badge', 'O Novo Padrão em Publicação')}</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-serif leading-[1.1] tracking-tight mb-6 text-primary">
              {t('landing.hero.titleLine1', 'Dê vida à sua história com talentos de')}{' '}
              <span className="italic text-accent">{t('landing.hero.titleHighlight', 'classe mundial')}</span>.
            </h1>
            <p className="text-lg mb-8 leading-relaxed max-w-xl text-muted-foreground">
              {t(
                'landing.hero.description',
                'A reformu.lat conecta autores visionários a editores, designers e profissionais de marketing de elite. Crie livros incríveis juntos em um espaço colaborativo e seguro.',
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onSignup}
                className="landing-sheen inline-flex justify-center items-center px-8 py-4 rounded-full font-medium transition-all group hover:shadow-lg bg-primary text-primary-foreground hover:opacity-90"
              >
                {t('landing.hero.ctaFindProfessional', 'Encontrar um Profissional')}
                <ArrowRight
                  size={18}
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                />
              </button>
              <button
                onClick={onSignup}
                className="inline-flex justify-center items-center px-8 py-4 rounded-full font-medium border-2 transition-colors hover:bg-muted border-border text-foreground bg-card"
              >
                {t('landing.hero.ctaOfferServices', 'Oferecer seus Serviços')}
              </button>
            </div>

            {/* Ritual flourish — a lit candle foreshadows the "As cerimônias" section below */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="mt-6 flex items-center gap-2.5"
            >
              <span className="candle-mini shrink-0" style={{ transform: 'scale(0.82)' }} aria-hidden>
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 1.3, type: 'spring', stiffness: 220, damping: 11 }}
                  style={{ position: 'absolute', inset: 0, transformOrigin: '50% 58%' }}
                >
                  <span className="fl" style={{ position: 'absolute' }} />
                </motion.span>
                <span className="wk" />
                <span className="wx" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t('landing.hero.ritualCaption', 'a cerimônia começa na sua primeira palavra')} <span className="text-accent">✦</span>
              </span>
            </motion.div>

            <div className="mt-6 flex items-center gap-3 lg:gap-4 text-xs lg:text-sm text-muted-foreground">
              <div className="flex -space-x-2">
                {[12, 32, 45, 5, 68].map((i) => (
                  <img
                    key={i}
                    src={`https://i.pravatar.cc/100?img=${i}`}
                    alt={t('landing.hero.avatarAlt', 'Autor')}
                    className="w-7 h-7 lg:w-9 lg:h-9 rounded-full border-2 border-background object-cover"
                    referrerPolicy="no-referrer"
                  />
                ))}
              </div>
              <p>{t('landing.hero.trustedBy', 'Confiado por mais de 10.000 autores e profissionais')}</p>
            </div>
          </motion.div>

          {/* Animated chapter card stack */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="relative flex items-center justify-center snap-center shrink-0 w-[88%] lg:w-auto lg:h-[600px] lg:pr-6 lg:shrink"
          >
            <ChapterCardStack />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// DualPerspective
// ---------------------------------------------------------------------------
export const DualPerspective = () => {
  const { t } = useTranslation();
  return (
    <section className="py-10 md:py-20 bg-card" id="how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-12"
        >
          <h2 className="text-3xl font-serif mb-4 text-primary">
            {t('landing.dualPerspective.title', 'Um marketplace feito para os dois lados da história.')}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            {t(
              'landing.dualPerspective.subtitle',
              'Seja escrevendo seu romance de estreia ou polindo a obra-prima de outra pessoa, a reformu.lat oferece as ferramentas que você precisa para ter sucesso.',
            )}
          </p>
        </motion.div>

        {/* Mobile: horizontal slider of 2 panels. Desktop: 2-col grid */}
        <div
          className="
            flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-3
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            lg:grid lg:grid-cols-2 lg:gap-8 lg:overflow-visible lg:mx-0 lg:px-0 lg:pb-0
            items-stretch
          "
        >
          {/* For Authors — filled "primary" panel */}
          <motion.div
            id="authors"
            className="
              relative overflow-hidden rounded-3xl bg-primary p-6 md:p-10 text-primary-foreground shadow-xl shadow-primary/10
              snap-center shrink-0 w-[82%] sm:w-[46%]
              lg:w-auto lg:shrink
            "
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-primary-foreground/10 blur-3xl" />
            <div className="relative">
              <div className="mb-4 md:mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 md:h-11 md:w-11 items-center justify-center rounded-xl bg-primary-foreground/15">
                  <PenTool size={20} />
                </span>
                <span className="font-semibold tracking-wider uppercase text-sm text-primary-foreground/80">
                  {t('landing.dualPerspective.authorsLabel', 'Para Autores')}
                </span>
              </div>
              <h3 className="text-xl md:text-3xl font-serif mb-3 md:mb-4">
                {t('landing.dualPerspective.authorsTitle', 'Monte seu time dos sonhos.')}
              </h3>
              <p className="text-primary-foreground/80 mb-5 md:mb-8 text-sm md:text-base">
                {t(
                  'landing.dualPerspective.authorsDescription',
                  'Encontre o editor, designer ou profissional de marketing ideal para elevar seu manuscrito. Explore portfólios verificados, solicite orçamentos e colabore sem fricção.',
                )}
              </p>

              <ul className="space-y-3 md:space-y-5">
                {[
                  {
                    title: t('landing.dualPerspective.authorsFeature1Title', 'Profissionais Verificados'),
                    desc: t('landing.dualPerspective.authorsFeature1Desc', 'Apenas os 5% melhores candidatos são aceitos.'),
                  },
                  {
                    title: t('landing.dualPerspective.authorsFeature2Title', 'Pagamentos Seguros'),
                    desc: t(
                      'landing.dualPerspective.authorsFeature2Desc',
                      'Os fundos ficam retidos em segurança até você aprovar o trabalho.',
                    ),
                  },
                  {
                    title: t('landing.dualPerspective.authorsFeature3Title', 'Espaço Colaborativo'),
                    desc: t(
                      'landing.dualPerspective.authorsFeature3Desc',
                      'Compartilhe arquivos, troque mensagens e revise rascunhos em um só lugar.',
                    ),
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="mt-0.5 flex h-7 w-7 md:h-8 md:w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 text-sm font-semibold text-primary-foreground">
                      {i + 1}
                    </div>
                    <div className="ml-3 md:ml-4">
                      <h4 className="text-base md:text-lg font-medium">{item.title}</h4>
                      <p className="mt-0.5 text-xs md:text-sm text-primary-foreground/70">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 md:mt-8">
                <a
                  href="/app?auth=signup"
                  className="inline-flex items-center font-medium text-primary-foreground transition-opacity hover:opacity-80"
                >
                  {t('landing.dualPerspective.exploreProfessionals', 'Explorar profissionais')} <ChevronRight size={16} className="ml-1" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* For Professionals — light "accent" panel */}
          <motion.div
            id="professionals"
            className="
              relative overflow-hidden rounded-3xl border border-accent/25 bg-accent/5 p-6 md:p-10
              snap-center shrink-0 w-[82%] sm:w-[46%]
              lg:w-auto lg:shrink
            "
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-accent/10 blur-3xl" />
            <div className="relative">
              <div className="mb-4 md:mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 md:h-11 md:w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <Users size={20} />
                </span>
                <span className="font-semibold tracking-wider uppercase text-sm text-accent">
                  {t('landing.dualPerspective.professionalsLabel', 'Para Profissionais')}
                </span>
              </div>
              <h3 className="text-xl md:text-3xl font-serif mb-3 md:mb-4 text-primary">
                {t('landing.dualPerspective.professionalsTitle', 'Faça seu melhor trabalho, do seu jeito.')}
              </h3>
              <p className="text-muted-foreground mb-5 md:mb-8 text-sm md:text-base">
                {t(
                  'landing.dualPerspective.professionalsDescription',
                  'Mostre seu portfólio a milhares de autores que buscam ativamente suas habilidades. Defina suas tarifas, gerencie projetos e receba no prazo.',
                )}
              </p>

              <ul className="space-y-3 md:space-y-5">
                {[
                  {
                    title: t('landing.dualPerspective.professionalsFeature1Title', 'Leads Qualificados'),
                    desc: t(
                      'landing.dualPerspective.professionalsFeature1Desc',
                      'Os autores chegam prontos para contratar e colaborar.',
                    ),
                  },
                  {
                    title: t('landing.dualPerspective.professionalsFeature2Title', 'Pagamentos Garantidos'),
                    desc: t(
                      'landing.dualPerspective.professionalsFeature2Desc',
                      'Nunca mais persiga uma fatura. A gente cuida da cobrança.',
                    ),
                  },
                  {
                    title: t('landing.dualPerspective.professionalsFeature3Title', 'Ferramentas Profissionais'),
                    desc: t(
                      'landing.dualPerspective.professionalsFeature3Desc',
                      'Gerencie contratos, marcos e comunicações com facilidade.',
                    ),
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="mt-0.5 flex h-7 w-7 md:h-8 md:w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/15 text-sm font-semibold text-accent">
                      {i + 1}
                    </div>
                    <div className="ml-3 md:ml-4">
                      <h4 className="text-base md:text-lg font-medium text-primary">{item.title}</h4>
                      <p className="mt-0.5 text-xs md:text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 md:mt-8">
                <a
                  href="/join-professional"
                  className="inline-flex items-center font-medium text-accent transition-opacity hover:opacity-80"
                >
                  {t('landing.dualPerspective.applyAsProfessional', 'Candidatar-se como profissional')} <ChevronRight size={16} className="ml-1" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Swipe hint — mobile only */}
        <p className="mt-3 text-center text-xs text-muted-foreground/60 lg:hidden" aria-hidden="true">
          {t('landing.dualPerspective.swipeHint', 'Deslize para ver mais')}
        </p>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// ServicesGrid
// ---------------------------------------------------------------------------
export const ServicesGrid = () => {
  const { t } = useTranslation();
  const services = [
    {
      slug: 'editing',
      title: t('landing.servicesGrid.editingTitle', 'Edição'),
      desc: t('landing.servicesGrid.editingDesc', 'Edição de desenvolvimento, copidesque e revisão.'),
    },
    {
      slug: 'design',
      title: t('landing.servicesGrid.designTitle', 'Design'),
      desc: t('landing.servicesGrid.designDesc', 'Design de capa, diagramação de miolo e ilustrações.'),
    },
    {
      slug: 'marketing',
      title: t('landing.servicesGrid.marketingTitle', 'Marketing'),
      desc: t('landing.servicesGrid.marketingDesc', 'Estratégias de lançamento, anúncios e plataforma de autor.'),
    },
    {
      slug: 'ghostwriting',
      title: t('landing.servicesGrid.ghostwritingTitle', 'Ghostwriting'),
      desc: t('landing.servicesGrid.ghostwritingDesc', 'Dê vida às suas ideias com escritores profissionais.'),
    },
  ];

  return (
    <section className="py-10 md:py-20 bg-primary" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-10"
        >
          <div className="max-w-2xl">
            <h2 className="text-3xl font-serif mb-3 md:mb-4 text-primary-foreground">
              {t('landing.servicesGrid.title', 'Tudo que você precisa para publicar com excelência.')}
            </h2>
            <p className="text-base md:text-lg text-primary-foreground/70">
              {t('landing.servicesGrid.subtitle', 'Encontre profissionais especializados em cada etapa da jornada de publicação.')}
            </p>
          </div>
          <a
            href="/services"
            className="hidden md:flex items-center text-primary-foreground hover:opacity-80 transition-colors mt-6 md:mt-0"
          >
            {t('landing.servicesGrid.viewAllCategories', 'Ver todas as categorias')} <ArrowRight size={16} className="ml-2" />
          </a>
        </motion.div>

        {/* Mobile: 2-col compact grid. Desktop: 4-col grid */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              viewport={{ once: true }}
              className="
                group cursor-pointer overflow-hidden rounded-xl transition-all duration-300
                hover:-translate-y-1 hover:shadow-2xl
                bg-primary-foreground/5 border border-primary-foreground/10
                hover:border-primary-foreground/25 lg:rounded-2xl
              "
            >
              {/* Service illustration */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={`/services/${service.slug}.webp`}
                  alt={service.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
              </div>
              <div className="p-4 lg:p-5">
                <h3 className="text-sm font-medium text-primary-foreground lg:text-lg">{service.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-primary-foreground/70 lg:text-sm">{service.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <a
          href="/services"
          className="md:hidden flex items-center justify-center text-primary-foreground hover:opacity-80 transition-colors mt-6"
        >
          {t('landing.servicesGrid.viewAllCategories', 'Ver todas as categorias')} <ArrowRight size={16} className="ml-2" />
        </a>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// AIFeatures — Sticky-scroll showcase
//
// HOW TO SWAP MEDIA:
// Each feature has a `media` field (string path). The MediaPanel component
// automatically detects the file type:
//   - Ends in .mp4 or .webm  → renders <video autoPlay muted loop playsInline>
//   - Ends in .gif/.png/.jpg/.webp → renders <img>
//   - Empty string or file not found → renders an elegant themed placeholder
//
// To use real media, update the `media` field, e.g.:
//   media: '/assets/ai/ghostwriter.mp4'
//   media: '/assets/ai/writing-assistant.webm'
//   media: '/assets/ai/grammar.gif'
// ---------------------------------------------------------------------------

interface AIFeatureItem {
  icon: React.ReactNode;
  title: string;
  desc: string;
  badge: string;
  slug: string;
  /** Path to media file. Empty string = placeholder. */
  media: string;
  placeholderGradient: string;
}

// Renders the media panel content — video, image, or elegant placeholder
function MediaContent({ feature }: { feature: AIFeatureItem }) {
  const { t } = useTranslation();
  const [mediaError, setMediaError] = useState(false);

  const isVideo = /\.(mp4|webm)$/i.test(feature.media);
  const isImage = /\.(gif|png|jpg|jpeg|webp)$/i.test(feature.media);
  const showPlaceholder = !feature.media || mediaError;

  if (!showPlaceholder && isVideo) {
    return (
      <video
        key={feature.slug}
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-full object-cover"
        onError={() => setMediaError(true)}
      >
        <source src={feature.media} type={feature.media.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
      </video>
    );
  }

  if (!showPlaceholder && isImage) {
    return (
      <img
        key={feature.slug}
        src={feature.media}
        alt={feature.title}
        className="w-full h-full object-cover"
        onError={() => setMediaError(true)}
      />
    );
  }

  // Elegant placeholder — themed gradient + icon + label
  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center gap-4
                  bg-gradient-to-br ${feature.placeholderGradient}`}
    >
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center
                      bg-primary/10 text-primary border border-primary/20">
        {feature.icon}
      </div>
      <span className="text-sm font-medium text-muted-foreground tracking-wide">
        {feature.title}
      </span>
      <span className="text-[10px] text-muted-foreground/60 font-mono uppercase tracking-wider">
        {t('landing.aiFeatures.previewComingSoon', 'Prévia em breve')}
      </span>
    </div>
  );
}

// Browser-window chrome wrapper for the sticky media panel
function BrowserFrame({ children, compact }: { children: React.ReactNode; compact?: boolean }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
      {/* Title bar with traffic lights */}
      <div className={`flex items-center gap-2 border-b border-border bg-card ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}>
        <span className="w-3 h-3 rounded-full bg-destructive/60" />
        <span className="w-3 h-3 rounded-full bg-accent/50" />
        <span className="w-3 h-3 rounded-full bg-primary/40" />
        <div className="flex-1 mx-4">
          <div className={`rounded-md bg-muted/60 flex items-center px-3 gap-2 max-w-[240px] mx-auto ${compact ? 'h-4' : 'h-5'}`}>
            <Monitor size={10} className="text-muted-foreground/50 shrink-0" />
            <span className="text-[10px] text-muted-foreground/50 font-mono truncate">
              reformu.lat/editor
            </span>
          </div>
        </div>
      </div>
      {/* Content area — aspect-video */}
      <div className="aspect-video overflow-hidden relative">
        {children}
      </div>
    </div>
  );
}

// Single feature row used on desktop (scroll item)
function FeatureRow({
  feature,
  isActive,
  setRef,
}: {
  feature: AIFeatureItem;
  isActive: boolean;
  setRef: (node: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={setRef}
      className="py-10 lg:py-0 lg:min-h-[78vh] lg:flex lg:flex-col lg:justify-center"
    >
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, delay: 0.05 }}
        viewport={{ once: true }}
        className={`p-7 rounded-2xl border transition-all duration-400 cursor-default
                    ${isActive
                      ? 'border-primary/40 bg-primary/5 shadow-lg shadow-primary/8'
                      : 'border-border bg-card hover:border-primary/20 hover:bg-primary/3'
                    }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300
                           ${isActive
                             ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30 scale-105'
                             : 'bg-accent/10 text-accent'
                           }`}>
            {feature.icon}
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-all
                            ${isActive
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-primary/8 text-primary'
                            }`}>
            {feature.badge}
          </span>
        </div>
        <h3 className="text-xl font-semibold mb-2 text-primary">{feature.title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{feature.desc}</p>
      </motion.div>
    </div>
  );
}

// Mobile-only AI Feature slide card
function AIFeatureSlide({ feature }: { feature: AIFeatureItem }) {
  return (
    <div className="snap-center shrink-0 w-[85%] sm:w-[46%] flex flex-col gap-3">
      {/* Compact browser frame */}
      <BrowserFrame compact>
        <MediaContent feature={feature} />
      </BrowserFrame>
      {/* Text card below frame */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent/10 text-accent">
            {feature.icon}
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/8 text-primary">
            {feature.badge}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-primary mb-1">{feature.title}</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">{feature.desc}</p>
      </div>
    </div>
  );
}

export const AIFeatures = ({ onSignup }: { onSignup: () => void }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  // Mobile slider dot state
  const [mobileIndex, setMobileIndex] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const aiFeatures: AIFeatureItem[] = [
    {
      icon: <Mic size={28} />,
      title: t('landing.aiFeatures.ghostwriterTitle', 'Ghostwriter com IA'),
      desc: t(
        'landing.aiFeatures.ghostwriterDesc',
        'Transforme gravações de voz em rascunhos refinados. Fale suas ideias, deixe a IA estruturá-las e depois refine com ghostwriters profissionais.',
      ),
      badge: t('landing.aiFeatures.ghostwriterBadge', 'Revolucionário'),
      slug: 'ghostwriter',
      // Replace with real path when ready, e.g. '/assets/ai/ghostwriter.mp4'
      media: '/assets/ai/ghostwriter.mp4',
      placeholderGradient: 'from-primary/30 via-accent/20 to-background',
    },
    {
      icon: <Wand2 size={28} />,
      title: t('landing.aiFeatures.writingAssistantTitle', 'Assistente de Escrita IA'),
      desc: t(
        'landing.aiFeatures.writingAssistantDesc',
        'Aprimore sua prosa com sugestões inteligentes. Melhore, expanda, condense ou ajuste o tom com um único clique.',
      ),
      badge: t('landing.aiFeatures.writingAssistantBadge', 'No Editor'),
      slug: 'writing-assistant',
      media: '/assets/ai/writing-assistant.mp4',
      placeholderGradient: 'from-accent/30 via-primary/20 to-background',
    },
    {
      icon: <Brain size={28} />,
      title: t('landing.aiFeatures.grammarTitle', 'Gramática & Estilo'),
      desc: t(
        'landing.aiFeatures.grammarDesc',
        'Verificação gramatical profissional em português e inglês. Aperfeiçoe seu manuscrito antes de enviar aos editores.',
      ),
      badge: t('landing.aiFeatures.grammarBadge', 'Multilíngue'),
      slug: 'grammar',
      media: '/assets/ai/grammar.mp4',
      placeholderGradient: 'from-primary/20 via-accent/30 to-background',
    },
    {
      icon: <Zap size={28} />,
      title: t('landing.aiFeatures.instantDraftsTitle', 'Rascunhos Instantâneos'),
      desc: t(
        'landing.aiFeatures.instantDraftsDesc',
        'Gere esboços de capítulos e primeiros rascunhos a partir de transcrições ou notas. A IA faz o trabalho pesado, você adiciona a magia.',
      ),
      badge: t('landing.aiFeatures.instantDraftsBadge', 'Rápido'),
      slug: 'instant-drafts',
      media: '/assets/ai/instant-drafts.mp4',
      placeholderGradient: 'from-accent/20 via-primary/25 to-background',
    },
  ];

  // ── Desktop sticky-scroll: active = the feature row whose CENTER is closest to
  // the viewport center. Pure, deterministic math on scroll (no flaky in-view
  // thresholds, no flicker, exactly one active at a time). Desktop (lg) only.
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    let raf = 0;

    const compute = () => {
      const viewportCenter = window.innerHeight / 2;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < rowRefs.current.length; i++) {
        const node = rowRefs.current[i];
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        const dist = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      setActiveIndex((prev) => (prev === best ? prev : best));
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    const enable = () => {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      compute();
    };
    const disable = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };

    if (mq.matches) enable();
    const onMqChange = (e: MediaQueryListEvent) => (e.matches ? enable() : disable());
    mq.addEventListener('change', onMqChange);

    return () => {
      disable();
      mq.removeEventListener('change', onMqChange);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Track mobile scroll position to update dots
  const handleMobileScroll = () => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    const scrollLeft = el.scrollLeft;
    const itemWidth = el.scrollWidth / aiFeatures.length;
    const idx = Math.round(scrollLeft / itemWidth);
    setMobileIndex(Math.min(Math.max(idx, 0), aiFeatures.length - 1));
  };

  return (
    <section className="py-10 md:py-20 relative overflow-x-clip bg-background" id="ai-features">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20 bg-accent blur-[80px]" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10 bg-primary blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6 bg-accent/10 text-accent">
            <Sparkles size={16} className="fill-current" />
            <span>{t('landing.aiFeatures.badge', 'Escrita com IA')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif mb-6 text-primary">
            {t('landing.aiFeatures.titleLine1', 'Escreva mais rápido. Escreva')}{' '}
            <span className="italic text-accent">{t('landing.aiFeatures.titleHighlight', 'melhor')}</span>.
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
            {t(
              'landing.aiFeatures.description',
              'Nossas ferramentas de IA ampliam sua criatividade, não a substituem. Receba assistência inteligente em cada etapa da sua jornada de escrita.',
            )}
          </p>
        </motion.div>

        {/* MOBILE: horizontal slider of AI feature cards (lg:hidden) */}
        <div className="lg:hidden mb-10">
          <div
            ref={sliderRef}
            onScroll={handleMobileScroll}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {aiFeatures.map((feature) => (
              <AIFeatureSlide key={`mobile-ai-${feature.slug}`} feature={feature} />
            ))}
          </div>
          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5 mt-3" role="tablist" aria-label="AI features">
            {aiFeatures.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === mobileIndex}
                aria-label={`Feature ${i + 1}`}
                onClick={() => {
                  if (!sliderRef.current) return;
                  const itemWidth = sliderRef.current.scrollWidth / aiFeatures.length;
                  sliderRef.current.scrollTo({ left: itemWidth * i, behavior: 'smooth' });
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === mobileIndex ? 'w-6 bg-primary' : 'w-1.5 bg-border'
                }`}
              />
            ))}
          </div>
        </div>

        {/* DESKTOP: Sticky-scroll layout — 2 cols */}
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start mb-14">

          {/* LEFT: Sticky media panel (desktop only) — pinned & vertically centered */}
          <div className="lg:flex lg:flex-col lg:justify-center lg:sticky lg:top-0 lg:h-screen">
            {/* Progress dots */}
            <div className="flex gap-2 mb-5 justify-end">
              {aiFeatures.map((_, i) => (
                <button
                  key={i}
                  onClick={() =>
                    rowRefs.current[i]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                  }
                  aria-label={`Feature ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300
                               ${i === activeIndex
                                 ? 'w-8 bg-primary'
                                 : 'w-1.5 bg-border hover:bg-primary/40'
                               }`}
                />
              ))}
            </div>

            {/* Browser frame with crossfade */}
            <BrowserFrame>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: 'easeInOut' }}
                  className="absolute inset-0"
                >
                  <MediaContent feature={aiFeatures[activeIndex]} />
                </motion.div>
              </AnimatePresence>
            </BrowserFrame>

            {/* Vertical progress bar */}
            <div className="mt-5 flex items-center gap-3">
              <div className="flex-1 h-1 rounded-full bg-border overflow-hidden">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  animate={{ width: `${((activeIndex + 1) / aiFeatures.length) * 100}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
              <span className="text-xs text-muted-foreground font-mono tabular-nums">
                {activeIndex + 1}/{aiFeatures.length}
              </span>
            </div>
          </div>

          {/* RIGHT: Scrollable feature list */}
          <div>
            {aiFeatures.map((feature, index) => (
              <FeatureRow
                key={feature.slug}
                feature={feature}
                isActive={activeIndex === index}
                setRef={(node) => {
                  rowRefs.current[index] = node;
                }}
              />
            ))}
            {/* Trailing scroll room so the LAST feature can rise to the top of the
                viewport while the media panel stays sticky-centered (matches the
                others' behavior). */}
            <div className="h-[30vh]" aria-hidden="true" />
          </div>
        </div>

        {/* AI + Professionals banner — text left, illustration right (bleeds out) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="relative rounded-3xl bg-primary px-8 py-10 md:px-12 md:py-12"
        >
          <div className="grid items-center gap-6 md:grid-cols-2 md:gap-10">

            {/* LEFT: text */}
            <div className="relative z-10 text-center md:text-left">
              {/* Mobile illustration (in-flow, contained) */}
              <img
                src="/ai-pros.webp"
                alt={t('landing.aiFeatures.bannerImageAlt', 'IA e profissionais humanos colaborando')}
                className="mx-auto mb-6 w-[65%] max-w-[250px] drop-shadow-xl md:hidden"
              />

              <h3 className="mb-4 font-serif text-2xl text-primary-foreground md:text-3xl">
                {t('landing.aiFeatures.bannerTitle', 'IA + Profissionais Humanos = Excelência')}
              </h3>
              <p className="mb-8 max-w-xl text-primary-foreground/70 md:mx-0 mx-auto">
                {t(
                  'landing.aiFeatures.bannerDescription',
                  'Nossa IA acelera seu trabalho, mas o polimento final vem de profissionais reais. Use a IA para primeiros rascunhos e depois contrate editores e designers especialistas no nosso marketplace.',
                )}
              </p>

              <motion.button
                onClick={onSignup}
                whileHover={{ scale: 1.05, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-semibold text-accent-foreground transition-colors"
              >
                <Sparkles size={18} />
                {t('landing.aiFeatures.bannerCta', 'Testar Recursos de IA Grátis')}
              </motion.button>
            </div>

            {/* RIGHT: illustration that intentionally bleeds out of the card (desktop) */}
            <div className="relative hidden md:block">
              <motion.img
                src="/ai-pros.webp"
                alt={t('landing.aiFeatures.bannerImageAlt', 'IA e profissionais humanos colaborando')}
                aria-hidden
                initial={{ opacity: 0, x: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                viewport={{ once: true }}
                className="pointer-events-none absolute top-1/2 right-[-4%] w-[115%] max-w-none -translate-y-1/2 drop-shadow-2xl"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Pricing — teaser pointing to the full /pricing page with add-ons
// ---------------------------------------------------------------------------
export const Pricing = ({ onSignup }: { onSignup: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency, toggle: toggleCurrency, fmt, monthly } = useCurrency();
  const shouldReduceMotion = useReducedMotion();

  const addons = [
    {
      slug: 'ia-studio',
      id: 'ia-studio',
      name: t('landing.pricing.addonIaStudioName', 'IA Studio'),
      desc: t('landing.pricing.addonIaStudioDesc', 'IA de escrita (≈1.500 ações/mês) + 10 capas por mês.'),
    },
    {
      slug: 'craft',
      id: 'craft',
      name: t('landing.pricing.addonCraftName', 'Craft'),
      desc: t('landing.pricing.addonCraftDesc', 'Histórico de versões, estatísticas, metas e EPUB.'),
    },
    {
      slug: 'outlining',
      id: 'outlining',
      name: t('landing.pricing.addonOutliningName', 'Outlining'),
      desc: t('landing.pricing.addonOutliningDesc', 'Boards, notas com atributos e templates.'),
    },
    {
      slug: 'bundle',
      id: 'bundle',
      name: t('landing.pricing.addonBundleName', 'Bundle "Tudo"'),
      desc: t('landing.pricing.addonBundleDesc', 'Todos os add-ons juntos — melhor valor.'),
      highlight: true,
    },
  ];

  const ICON_SIZE: Record<string, string> = {
    'ia-studio': 'h-[11rem] w-[11rem]',
    'craft': 'h-[12.65rem] w-[12.65rem]',
    'outlining': 'h-[10.45rem] w-[10.45rem]',
    'bundle': 'h-[11rem] w-[11rem]',
  };

  return (
    <section className="relative overflow-x-hidden bg-card py-16 md:py-24" id="pricing">
      {/* Moonlit glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-72 w-[44rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[110px]" />
        <div className="absolute bottom-0 right-1/4 h-56 w-72 rounded-full bg-accent/10 blur-[90px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          viewport={{ once: true }}
          className="mx-auto mb-12 max-w-2xl text-center md:mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent" />
              {t('landing.pricing.badge', 'Preços à la carte')}
            </span>
            {/* Currency toggle */}
            <button
              onClick={toggleCurrency}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border bg-background font-mono text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all"
              aria-label={t('landing.pricing.currencyToggleAriaLabel', {
                currency:
                  currency === 'USD'
                    ? t('landing.pricing.currencyReal', 'Real (BRL)')
                    : t('landing.pricing.currencyDollar', 'Dólar (USD)'),
                defaultValue: 'Trocar moeda para {{currency}}',
              })}
            >
              <span className={currency === 'USD' ? 'text-foreground' : ''}>US$</span>
              <span className="text-border px-0.5">/</span>
              <span className={currency === 'BRL' ? 'text-foreground' : ''}>R$</span>
            </button>
          </div>
          <h2 className="mb-3 font-serif text-3xl tracking-tight text-foreground md:text-4xl">
            {t('landing.pricing.titleLine1', 'Pague só pelo que')}{' '}
            <span className="italic text-accent">{t('landing.pricing.titleHighlight', 'precisa')}</span>.
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
            {t('landing.pricing.subtitle', 'Comece grátis e empilhe add-ons independentes. Sem pacotes inchados, sem surpresas.')}
          </p>
        </motion.div>

        {/* Add-on cards — horizontal slider below desktop, 4-col grid on lg+ */}
        <div className="
          flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pt-24 pb-3
          [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:mx-0 lg:px-0 lg:pt-24 lg:pb-0
        ">
          {addons.map((a, idx) => {
            const price = monthly(a.id);
            return (
              <motion.div
                key={a.name}
                initial={shouldReduceMotion ? undefined : { opacity: 0, y: 24 }}
                whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.09, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={shouldReduceMotion ? undefined : { y: -4 }}
                className={cn(
                  'group relative flex flex-col rounded-2xl border p-5 pt-[5.5rem] transition-[colors,box-shadow] duration-300',
                  'snap-center shrink-0 w-[82%] sm:w-[48%] lg:w-auto lg:shrink-0',
                  a.highlight
                    ? 'border-accent/40 bg-gradient-to-b from-accent/[0.08] to-card ring-1 ring-accent/20 shadow-lg shadow-accent/5'
                    : 'border-border bg-background/70 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5',
                )}
              >
                {/* Breathing moonlit glow — highlighted "best value" card only. Extremely
                    subtle; sits behind the sheen/content layers, ignored under reduced motion. */}
                {a.highlight && !shouldReduceMotion && (
                  <motion.div
                    aria-hidden
                    className="rounded-2xl"
                    style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                    animate={{
                      boxShadow: [
                        '0 0 0px 0px rgba(246,233,200,0)',
                        '0 0 26px 2px rgba(246,233,200,0.12)',
                        '0 0 0px 0px rgba(246,233,200,0)',
                      ],
                    }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}

                {/* Sheen sweep — bounded to the card's own face (not the bleeding icon,
                    which is a sibling and stays outside this layer's overflow clip).
                    Position is forced inline (beats .landing-sheen's own `position:relative`
                    of equal specificity — see candle-mini for the same pattern). */}
                <div
                  aria-hidden
                  className="landing-sheen rounded-2xl"
                  style={{ position: 'absolute', inset: 0 }}
                />

                {/* Featured badge */}
                {a.highlight && (
                  <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-accent-foreground">
                    <Star className="h-2.5 w-2.5 fill-current" />
                    {t('landing.pricing.bestValueBadge', 'Melhor valor')}
                  </span>
                )}

                {/* Add-on illustrated icon — bleeds above the card top-left edge */}
                <img
                  src={`/addons/${a.slug}.webp`}
                  alt={a.name}
                  className={cn(
                    'pointer-events-none absolute z-20 -left-3 md:-left-2 top-0 -translate-y-1/2 object-contain drop-shadow-xl transition-transform duration-300 group-hover:scale-105',
                    ICON_SIZE[a.slug] ?? 'h-[11rem] w-[11rem]',
                  )}
                />

                <h3 className={cn('font-serif text-lg font-semibold', a.highlight ? 'text-accent' : 'text-foreground')}>
                  {a.name}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{a.desc}</p>

                {/* Price — animated on currency change */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${a.id}-${currency}`}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.18 }}
                    className="mt-5 flex items-baseline gap-1"
                  >
                    <span className={cn('font-serif text-2xl font-bold', a.highlight ? 'text-accent' : 'text-foreground')}>
                      {fmt(price)}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground">{t('landing.pricing.perMonth', '/mês')}</span>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Free plan banner */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="mt-6 flex flex-col items-center justify-between gap-5 rounded-2xl border border-border bg-background/60 p-5 backdrop-blur-sm sm:flex-row md:mt-8 md:p-6"
        >
          <div className="flex items-center gap-3 text-center sm:text-left">
            <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary sm:inline-flex">
              <PenLine className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <p className="font-serif text-base font-semibold text-foreground">
                {t('landing.pricing.freeForeverTitle', 'Grátis para sempre')}{' '}
                <span className="text-muted-foreground">— {fmt(0)}</span>
              </p>
              <p className="text-xs text-muted-foreground md:text-sm">
                {t('landing.pricing.freeForeverDescription', 'Editor completo · dark mode · export PDF/DOCX · 30 ações de IA por mês.')}
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto sm:flex-row">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/pricing')}
              className="rounded-full border border-border bg-card px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
            >
              {t('landing.pricing.viewAllPricing', 'Ver todos os preços')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onSignup}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider text-primary-foreground shadow-md shadow-primary/20 transition-opacity hover:opacity-90"
            >
              {t('landing.pricing.startFree', 'Começar Grátis')}
              <ArrowRight className="h-3.5 w-3.5" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Testimonials
// ---------------------------------------------------------------------------
export const Testimonials = () => {
  const { t } = useTranslation();
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const testimonialSliderRef = useRef<HTMLDivElement>(null);

  const testimonialData = [
    {
      avatarSrc: 'https://i.pravatar.cc/100?img=47',
      avatarAlt: 'Carla Mendonça',
      quote: t(
        'landing.testimonials.quote1',
        '"Encontrar o editor certo era assustador — até descobrir a reformu.lat. A interface é linda, os talentos são genuinamente de alto nível e as ferramentas de colaboração tornaram todo o processo uma alegria."',
      ),
      name: 'Carla Mendonça',
      role: t('landing.testimonials.role1', 'Autora Independente'),
    },
    {
      avatarSrc: 'https://i.pravatar.cc/100?img=33',
      avatarAlt: 'Rodrigo Alves',
      quote: t(
        'landing.testimonials.quote2',
        '"Como designer de capas freelancer, a reformu.lat transformou completamente o meu negócio. Não preciso mais correr atrás de faturas, e tenho a sorte de trabalhar com autores incrivelmente apaixonados."',
      ),
      name: 'Rodrigo Alves',
      role: t('landing.testimonials.role2', 'Designer de Capas'),
    },
  ];

  const handleTestimonialScroll = () => {
    if (!testimonialSliderRef.current) return;
    const el = testimonialSliderRef.current;
    const itemWidth = el.scrollWidth / testimonialData.length;
    const idx = Math.round(el.scrollLeft / itemWidth);
    setTestimonialIndex(Math.min(Math.max(idx, 0), testimonialData.length - 1));
  };

  return (
    <section className="py-10 md:py-20 overflow-hidden bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-3xl font-serif text-center mb-8 md:mb-10 text-primary"
        >
          {t('landing.testimonials.title', 'Histórias de sucesso.')}
        </motion.h2>

        {/* Mobile: horizontal slider. Desktop: 2-col grid */}
        <div
          ref={testimonialSliderRef}
          onScroll={handleTestimonialScroll}
          className="
            flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-3
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            md:grid md:grid-cols-2 md:gap-8 md:overflow-visible md:mx-0 md:px-0 md:pb-0
          "
        >
          {testimonialData.map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.07 }}
              viewport={{ once: true }}
              className="
                p-7 rounded-3xl shadow-sm relative bg-card border border-border
                hover:shadow-xl hover:-translate-y-1 hover:border-primary/20
                transition-all duration-300
                snap-center shrink-0 w-[82%] sm:w-[46%]
                md:w-auto md:shrink md:p-10
              "
            >
              <div className="absolute top-7 right-7 text-accent/20 md:top-10 md:right-10">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>
              <div className="flex items-center space-x-1 mb-4 text-accent md:mb-6">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={14} className="fill-current md:w-4 md:h-4" />
                ))}
              </div>
              <p className="text-base italic mb-5 relative z-10 text-foreground/80 md:text-lg md:mb-8">
                {item.quote}
              </p>
              <div className="flex items-center">
                <img
                  src={item.avatarSrc}
                  alt={item.avatarAlt}
                  className="w-10 h-10 rounded-full object-cover md:w-12 md:h-12"
                  referrerPolicy="no-referrer"
                />
                <div className="ml-3 md:ml-4">
                  <h4 className="font-medium text-primary text-sm md:text-base">{item.name}</h4>
                  <p className="text-xs text-muted-foreground md:text-sm">{item.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Dot indicators — mobile only */}
        <div
          className="flex justify-center gap-1.5 mt-3 md:hidden"
          role="tablist"
          aria-label={t('landing.testimonials.tablistAriaLabel', 'Depoimentos')}
        >
          {testimonialData.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === testimonialIndex}
              aria-label={t('landing.testimonials.dotAriaLabel', { number: i + 1, defaultValue: 'Depoimento {{number}}' })}
              onClick={() => {
                if (!testimonialSliderRef.current) return;
                const itemWidth = testimonialSliderRef.current.scrollWidth / testimonialData.length;
                testimonialSliderRef.current.scrollTo({ left: itemWidth * i, behavior: 'smooth' });
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === testimonialIndex ? 'w-6 bg-primary' : 'w-1.5 bg-border'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// CTA
// ---------------------------------------------------------------------------
export const CTA = ({ onSignup }: { onSignup: () => void }) => {
  const { t } = useTranslation();
  return (
    <section className="py-16 md:py-20 relative overflow-hidden bg-primary">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-foreground/5 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-3xl md:text-5xl font-serif text-primary-foreground mb-6"
        >
          {t('landing.cta.title', 'Pronto para começar seu próximo capítulo?')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-xl mb-8 max-w-2xl mx-auto text-primary-foreground/80"
        >
          {t(
            'landing.cta.description',
            'Junte-se à comunidade de autores e profissionais de publicação que criam livros incríveis juntos.',
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.18 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row justify-center gap-4"
        >
          <motion.button
            onClick={onSignup}
            whileHover={{ scale: 1.05, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}
            whileTap={{ scale: 0.97 }}
            className="landing-sheen px-8 py-4 rounded-full font-semibold text-lg transition-colors
                       bg-accent text-accent-foreground"
          >
            {t('landing.cta.signupAsAuthor', 'Cadastrar como Autor')}
          </motion.button>
          <motion.button
            onClick={onSignup}
            whileHover={{ scale: 1.04, boxShadow: '0 6px 24px rgba(0,0,0,0.14)' }}
            whileTap={{ scale: 0.97 }}
            className="px-8 py-4 rounded-full font-semibold text-lg transition-colors
                       bg-primary-foreground text-primary"
          >
            {t('landing.cta.applyAsProfessional', 'Candidatar-se como Profissional')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// LandingPage (root export — no props; self-contained)
// ---------------------------------------------------------------------------
export function LandingPage() {
  const navigate = useNavigate();

  const handleSignup = () => {
    navigate('/app?auth=signup');
  };

  return (
    <div className="min-h-screen font-sans bg-background text-foreground">
      <MoonGlowBackground />
      <LandingNav />
      <main>
        <Hero onSignup={handleSignup} />
        <Ceremonies />
        <DualPerspective />
        <ServicesGrid />
        <AIFeatures onSignup={handleSignup} />
        <Pricing onSignup={handleSignup} />
        <Testimonials />
        <CTA onSignup={handleSignup} />
      </main>
      <LandingFooter />
    </div>
  );
}
